/**
 * Single source of truth for the current user's `profiles` row.
 *
 * Centralizes what was previously 4+ separate React Query fetches
 * (display_name, subscription_tier_id, terms_accepted_at, scheduled_deletion_at, ...)
 * into one cached row. Other hooks (useUserTier, RequireTerms, AppHeader,
 * DeletionBanner, useTrialLimit) read from this cache instead of hitting
 * the DB independently on every mount/navigation.
 *
 * Cache key: ["profile", userId]. Invalidate after profile updates with:
 *   queryClient.invalidateQueries({ queryKey: ["profile", userId] });
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Tables } from "@/integrations/supabase/types";

export type ProfileRow = Tables<"profiles">;

export function useProfile() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["profile", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 min — profile changes rarely
    gcTime: 30 * 60 * 1000,
    queryFn: async (): Promise<ProfileRow | null> => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
